import React from "react";
import {
  Text,
  Avatar,
  Stack,
  HStack,
  Image,
  Box,
  Flex,
  Circle,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import {
  textSecondary,
  color8,
  primaryAlt,
  primary,
  secondary,
  buttonBlueText,
  buttonBlue,
} from "../../../../utils/colors";
import dayjs from "dayjs";
import StatusBadge from "../../../../components/badges/StatusBadge";
import MoreButtonLite from "../../../../components/buttons/MoreButtonLite";
import StatusButtonNewLite from "../../../../components/buttons/StatusButtonNewLite";
import { TbCake, TbEdit } from "react-icons/tb";
import StatusButtonNew from "../../../../components/buttons/StatusButtonNew";
import { updateEmployeeStatusAction } from "../../../../actions/employeeActions";

export const columns = (
  dispatch,
  slug,
  navigate,
  isMobile,
  textPrimaryMode,
  whiteMode,
  translate,
  handleSortChange,
  isAdmin,
  loggedInUserId,
  setShowDeleteConfirmation,
  setUserId,
  setEmployeeName,
  deleteHandler,
  setEmployeeIsCoordinatorInPlans,
  openEditEmployeeSidePanelModal
) => {
  // Function to format phone number

  // Function to check if the birthday is within a week
  const isBirthdayInAWeek = (birthDate) => {
    if (!birthDate) return false; // Handle undefined birthDate

    const today = dayjs();
    let birthdayThisYear = dayjs(birthDate).set("year", today.year()); // Preserve MM-DD

    // If birthday already passed but is today or within the next 7 days, keep it
    if (birthdayThisYear.isBefore(today, "day")) {
      if (birthdayThisYear.add(7, "day").isAfter(today)) {
        return true; // It's still within the next week
      }
      birthdayThisYear = birthdayThisYear.add(1, "year"); // Move to next year
    }

    return (
      birthdayThisYear.isAfter(today) &&
      birthdayThisYear.isBefore(today.add(7, "day"))
    );
  };

  // Function to calculate upcoming age
  const calculateUpcomingAge = (birthDate) => {
    const thisYear = dayjs().year();
    const birthYear = dayjs(birthDate).year();
    return thisYear - birthYear;
  };

  const allColumns = [
    {
      Header: () => (
        <Box onClick={() => handleSortChange("employeeID")}>
          <Flex align="center" cursor="pointer">
            <Text textAlign="left">{translate("patient.columnID")}</Text>
          </Flex>
        </Box>
      ),
      accessor: "employeeID",
      width: 100,
      disableSortBy: true,
      Cell: ({ row }) => {
        const { employeeID } = row.original;

        return (
          <Text
            textAlign="left"
            overflow="hidden"
            whiteSpace="nowrap"
            fontSize="xs"
            color={textSecondary}
            fontWeight="500"
          >
            {employeeID}
          </Text>
        );
      },
    },

    {
      Header: () => (
        <Flex h="100%" w="100%">
          <Flex align="center" cursor="pointer">
            <Text>{translate("employee.columnEmployee")}</Text>
          </Flex>
        </Flex>
      ),
      disableSortBy: true,
      width: 280,
      accessor: "lastName",
      Cell: ({ row }) => {
        const { firstName, lastName, image, userID, status, birthDate } =
          row.original;
        const name = `${lastName} ${firstName}`;

        const isCurrentUser = loggedInUserId === row.original._id;

        // fake birthDate for testing
        // const birthDate = dayjs().add(3, "days").format("YYYY-MM-DD"); // Birthday in 3 days

        // console.log("birthDate", birthDate);

        // check for upcoming birthday
        const formattedBirthDate = dayjs(birthDate).format("YYYY MMMM DD");
        const isBirthdayUpcoming = isBirthdayInAWeek(birthDate);
        const upcomingAge = calculateUpcomingAge(birthDate);

        return (
          <HStack gap={2}>
            {image ? (
              <Image
                size="sm"
                src={image}
                alt={name}
                borderRadius="10rem"
                objectFit="cover"
              />
            ) : (
              <>
                {!isMobile && (
                  <Avatar
                    h={["2.5rem", "2rem", "2rem"]}
                    w={["2.5rem", "2rem", "2rem"]}
                    name={name}
                    // bg="#dceeff"
                    // color="#3a74a9"
                    bg={!isCurrentUser ? color8 : secondary}
                    color={whiteMode}
                  />
                )}
              </>
            )}

            <Stack gap={0}>
              <Flex align="center">
                <Text
                  fontSize="md"
                  color={textPrimaryMode}
                  fontWeight="600"
                  isTruncated // This will add ellipsis
                  maxW="200px"
                >
                  {lastName}
                  <Text as="span" fontWeight="400" ml={1}>
                    {firstName}
                  </Text>
                </Text>
                {isBirthdayUpcoming && (
                  <Tooltip
                    p="0.5rem 1rem"
                    borderRadius="1rem"
                    placement="top"
                    label={
                      <Stack gap={0}>
                        <Text
                          fontSize="xs"
                          fontWeight="500"
                          textTransform="capitalize"
                        >
                          {translate("employee.birthdayInWeek")}:{" "}
                          {formattedBirthDate}
                        </Text>
                        <Text fontSize="xs" fontWeight="500">
                          {translate("employee.turns")}: {upcomingAge}{" "}
                          {translate("employee.yearsOld")}
                        </Text>
                      </Stack>
                    }
                  >
                    <Circle
                      ml={1}
                      h="18px"
                      w="18px"
                      bg={buttonBlue}
                      borderRadius="10rem"
                    >
                      <Icon h="0.75rem" as={TbCake} color={buttonBlueText} />
                    </Circle>
                  </Tooltip>
                )}
              </Flex>
              {isMobile && (
                <Flex align="baseline" m="0.25rem 0 0">
                  <StatusBadge isSmall status={status} options="user" />
                  <Text
                    fontSize="xs"
                    color={textSecondary}
                    fontWeight="500"
                    ml={2}
                  >
                    {userID}
                  </Text>
                </Flex>
              )}
            </Stack>
          </HStack>
        );
      },
    },

    // {
    //   Header: () => (
    //     <Box onClick={() => handleSortChange("lastName")}>
    //       <Flex align="center" cursor="pointer">
    //         <Text mr={2}>{translate("employee.columnEmployee")}</Text>
    //       </Flex>
    //     </Box>
    //   ),
    //   accessor: "lastName",
    //   disableSortBy: true,
    //   Cell: ({ row }) => {
    //     const { firstName, lastName, image, profession, birthDate } =
    //       row.original;
    //     const name = `${firstName} ${lastName}`;
    //     const formattedBirthDate = dayjs(birthDate).format("YYYY MMMM DD");
    //     const upcomingAge = calculateUpcomingAge(birthDate);

    //     const isCurrentUser = loggedInUserId === row.original._id;

    //     // console.log("profession", profession);

    //     // console.log("birthDate", birthDate);
    //     // console.log(
    //     //   "isBirthdayInAWeek(birthDate)",
    //     //   isBirthdayInAWeek(birthDate)
    //     // );

    //     return (
    //       <HStack gap={2}>
    //         {image ? (
    //           <Image
    //             h="2rem"
    //             w="2rem"
    //             src={image}
    //             alt={name}
    //             borderRadius="10rem"
    //             objectFit="cover"
    //           />
    //         ) : (
    //           <Tooltip
    //             bg={shade(0.6, secondary)}
    //             p="0.75rem 0.75rem"
    //             borderRadius="1rem"
    //             label={
    //               <>
    //                 <Text>
    //                   {translate("employee.currentlyLoggedIn")}
    //                   <Text as="span" ml={1} fontWeight="600">
    //                     {name}
    //                   </Text>
    //                 </Text>
    //               </>
    //             }
    //             isDisabled={!isCurrentUser}
    //           >
    //             <Avatar size="sm" name={name} bg="#dceeff" color="#3a74a9">
    //               {isCurrentUser && (
    //                 <AvatarBadge
    //                   borderColor="white"
    //                   bg={primary}
    //                   boxSize="1em"
    //                 />
    //               )}
    //             </Avatar>
    //           </Tooltip>
    //         )}

    //         <Stack gap={0}>
    //           <Text
    //             fontSize="md"
    //             color={textPrimary}
    //             fontWeight="600"
    //             isTruncated // This will add ellipsis
    //             whiteSpace="nowrap"
    //             w="100%"
    //             maxWidth="260px"
    //           >
    //             {lastName}

    //             <Text as="span" fontWeight="400" ml={1}>
    //               {firstName}
    //             </Text>
    //           </Text>
    //           <Text fontSize="xs" color={textSecondary} fontWeight="500">
    //             {profession && profession.name && profession.name}
    //           </Text>
    //         </Stack>
    //         {isBirthdayInAWeek(birthDate) && (
    //           <Tooltip
    //             label={
    //               <Stack>
    //                 <Text
    //                   fontSize="xs"
    //                   fontWeight="500"
    //                   textTransform="capitalize"
    //                 >
    //                   {translate("employee.birthdayInWeek")}:{" "}
    //                   {formattedBirthDate}
    //                 </Text>
    //                 <Text fontSize="xs" fontWeight="500">
    //                   {translate("employee.turns")}: {upcomingAge}{" "}
    //                   {translate("employee.yearsOld")}
    //                 </Text>
    //               </Stack>
    //             }
    //           >
    //             <Circle
    //               ml={1}
    //               h="18px"
    //               w="18px"
    //               bg={buttonBlue}
    //               borderRadius="10rem"
    //             >
    //               <Icon h="0.75rem" as={TbCake} color={buttonBlueText} />
    //             </Circle>
    //           </Tooltip>
    //         )}
    //       </HStack>
    //     );
    //   },
    // },
    {
      Header: () => (
        <Flex h="100%" w="100%">
          <Flex align="center" cursor="pointer">
            <Text>{translate("employee.columnProfession")}</Text>
          </Flex>
        </Flex>
      ),
      accessor: "profession",
      disableSortBy: true,
      Cell: ({ row }) => {
        const { profession } = row.original;

        return (
          <Flex align="center" w="100%">
            <Text
              fontSize="sm"
              color={textSecondary}
              fontWeight="500"
              whiteSpace="nowrap"
              isTruncated
              maxWidth="160px"
            >
              {(profession && profession.name && profession.name) || "-"}
            </Text>
          </Flex>
        );
      },
    },
    {
      Header: () => (
        <Flex h="100%" w="100%">
          <Flex align="center" cursor="pointer">
            <Text>{translate("employee.columnContacts")}</Text>
          </Flex>
        </Flex>
      ),
      accessor: "phone",
      disableSortBy: true,
      Cell: ({ row }) => {
        const { phone, email } = row.original;

        return (
          <>
            <Stack gap={0}>
              <Text
                textAlign="left"
                fontSize="sm"
                fontWeight="500"
                color={textSecondary}
                w="100%"
              >
                {phone ? phone : "-"}
              </Text>
              {/* <Text
                fontSize="xs"
                textAlign="left"
                color={textSecondary}
                fontWeight="500"
                maxWidth="300px"
                isTruncated
              >
                {email}
              </Text> */}
            </Stack>
          </>
        );
      },
    },

    // {
    //   Header: () => (
    //     <Box onClick={() => handleSortChange("phone")}>
    //       <Flex align="center" cursor="pointer">
    //         <Text mr={2}>{translate("employee.columnContacts")}</Text>
    //       </Flex>
    //     </Box>
    //   ),
    //   accessor: "phone",
    //   disableSortBy: true,
    //   Cell: ({ row }) => {
    //     const { phone, email } = row.original;

    //     return (
    //       <Stack gap={0}>
    //         <Text
    //           fontSize="15px"
    //           color={textPrimary}
    //           fontWeight="500"
    //           isTruncated // This will add ellipsis
    //           noOfLines={2} // This will limit the text to two lines
    //           whiteSpace="nowrap"
    //           overflow="hidden"
    //           w="100%"
    //           maxWidth="200px"
    //         >
    //           {formatPhoneNumber(phone)}
    //         </Text>
    //         <Text
    //           fontSize="xs"
    //           color={textSecondary}
    //           fontWeight="500"
    //           maxWidth="300px"
    //           isTruncated
    //         >
    //           {email}
    //         </Text>
    //       </Stack>
    //     );
    //   },
    // },
    // {
    //   Header: () => (
    //     <Box onClick={() => handleSortChange("createdAt")}>
    //       <Flex align="center" cursor="pointer">
    //         <Text mr={2}>{translate("employee.columnCreated")}</Text>
    //       </Flex>
    //     </Box>
    //   ),
    //   accessor: "createdAt",
    //   disableSortBy: true,
    //   Cell: ({ row }) => {
    //     const { createdAt } = row.original;

    //     return (
    //       <>
    //         {!isAdmin ? (
    //           <Image src={blank} size="sm" />
    //         ) : (
    //           <Text
    //             fontSize="sm"
    //             color={textSecondary}
    //             fontWeight="500"
    //             textTransform="capitalize"
    //           >
    //             {dayjs(createdAt).locale(translate("lang")).format("YY MMM DD")}
    //           </Text>
    //         )}
    //       </>
    //     );
    //   },
    // },
    // {
    //   Header: () => (
    //     <Box onClick={() => handleSortChange("rolePermission")}>
    //       <Flex align="center" cursor="pointer">
    //         <Text mr={2}>{translate("employee.columnRolePermission")}</Text>
    //       </Flex>
    //     </Box>
    //   ),
    //   accessor: "rolePermission",
    //   disableSortBy: true,
    //   Cell: ({ row }) => {
    //     const { rolePermission } = row.original;

    //     // console.log("row", row);

    //     return (
    //       <>
    //         <Text
    //           fontSize="sm"
    //           color={textSecondary}
    //           fontWeight="500"
    //           textTransform="capitalize"
    //           textAlign="left"
    //         >
    //           {translate(`rolePermission.${rolePermission}`)}
    //         </Text>
    //       </>
    //     );
    //   },
    // },
    {
      Header: () => (
        <Flex w="100%" h="100%">
          <Flex align="center" cursor="pointer" justify="flex-end" w="100%">
            <Text>{translate("patient.columnStatus")}</Text>
          </Flex>
        </Flex>
      ),
      accessor: "status",
      disableSortBy: true,
      preventRowClick: true,
      Cell: ({ row }) => {
        const { status, _id, isCoordinatorInPlans } = row.original;

        // const isCurrentUser = loggedInUserId === row.original._id;

        const handleUpdateStatus = (newStatus) => {
          dispatch(updateEmployeeStatusAction(slug, _id, newStatus));
        };

        return (
          <HStack gap={0} justify="flex-end">
            <StatusButtonNew
              currentStatus={status}
              onUpdateStatus={handleUpdateStatus}
              options="employee"
              p="0 2rem"
              // isUnclickable
            />
            {/* <StatusButtonNewLite
              p="0 3rem"
              size="md"
              currentStatus={status}
              options="employee"
            /> */}
            <MoreButtonLite
              id={_id}
              viewHandler={() => {
                setUserId(_id); // Set the user ID for viewing
                openEditEmployeeSidePanelModal(); // Open side panel modal
              }}
              viewLabel={translate("employee.editEmployee")}
              viewIcon={TbEdit}
              deleteHandler={true}
              // deleteButtonDisabled={!isAdmin || isCurrentUser}
              setShowDeleteConfirmation={() => {
                setUserId(_id); // Set the user ID for deletion
                setShowDeleteConfirmation(true); // Open confirmation modal
                setEmployeeIsCoordinatorInPlans(isCoordinatorInPlans);
                setEmployeeName(
                  row.original.lastName + " " + row.original.firstName
                );
              }}
            />

            {/* <MoreButtonLite
              id={_id}
              deleteHandler={true}
              setShowDeleteConfirmation={() => {
                // setUserId(_id); // Set the user ID for deletion
                // setShowDeleteConfirmation(true); // Open confirmation modal
                // setPatientName(
                //   row.original.lastName + " " + row.original.firstName
                // );
              }}
            /> */}
          </HStack>
        );
      },
    },

    // {
    //   Header: () => (
    //     <Box onClick={() => handleSortChange("status")} w="100%">
    //       <Flex align="center" cursor="pointer" justify="flex-end" w="100%">
    //         <Text mr={2}>{translate("patient.columnStatus")}</Text>
    //       </Flex>
    //     </Box>
    //   ),
    //   accessor: "status",
    //   disableSortBy: true,
    //   Cell: ({ row }) => {
    //     const { status, _id } = row.original;

    //     const handleUpdateStatus = (newStatus) => {
    //       dispatch(updateUserStatus(_id, newStatus));
    //     };

    //     const isCurrentUser = loggedInUserId === row.original._id;

    //     return (
    //       <HStack gap={0} justify="flex-end">
    //         <StatusButton
    //           currentStatus={status && status}
    //           onUpdateStatus={handleUpdateStatus}
    //           options="active"
    //           isUnclickable={!isAdmin}
    //         />
    //         <MoreButton
    //           id={_id}
    //           deleteHandler={deleteEmployeeHandler}
    //           deleteName={row.original.firstName + " " + row.original.lastName}
    //           deleteButtonDisabled={!isAdmin || isCurrentUser}
    //           editHandler={(e) => {
    //             e.stopPropagation();
    //             openEditEmployeeSidePanelModal(_id);
    //           }}
    //           editButtonDisabled={!isAdmin}
    //         />
    //       </HStack>
    //     );
    //   },
    // },
  ];

  return isAdmin
    ? allColumns
    : allColumns.filter((column) => column.accessor !== "rolePermission");
};
